import {
  createFileRoute,
  useNavigate,
  useRouteContext,
  useRouter,
} from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useMemo, type JSX } from "react";
import { z } from "zod";

import { RecurringPatientLimitsQueryDocument } from "@repo/graphql-types/graphql";
import Left from "@repo/icons/default/Left";

import { AppointmentCancelDrawer } from "@/components/appointment/drawer/appointment-cancel-drawer";
import { FlexiblePriceAppointmentCancelDrawer } from "@/components/appointment/flexible-price/flexible-price-appointment-cancel-drawer";
import { FlexiblePriceAppointmentRescheduleDrawer } from "@/components/appointment/flexible-price/flexible-price-appointment-reschedule-drawer";
import { AppointmentDrawer } from "@/components/appointment-drawer/appointment-drawer";
import { RecurrenceAppointmentTabs } from "@/components/recurring-appointment/recurrence-appointment-tabs";
import { RecurrencePatientHeaderCard } from "@/components/recurring-appointment/recurrence-patient-header-card";
import { RecurrencePatientLimitCard } from "@/components/recurring-appointment/recurrence-patient-limit-card";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import { recurringFlowOriginIndexAtom } from "@/lib/atoms/recurring-appointment-form-atom";
import { getHistoryIndex } from "@/lib/helpers/history";
import {
  buildRecurrenceSlots,
  classifyRecurrenceStatus,
  formatRecurrenceScheduleLabel,
  type RecurrenceAppointmentData,
} from "@/lib/helpers/recurrence";
import { isConsultorioVirtual } from "@/lib/helpers/unit";
import {
  mapNodeToRaw,
  RecurringAppointmentDetailPageQuery,
} from "@/lib/queries/recurrence-detail-query";
import { trackEvent } from "@/lib/tracking";

const RecurringAppointmentDetailPage = (): JSX.Element => {
  const { codAgendamentoRecorrente } = Route.useParams();
  const codRecorrencia = Number(codAgendamentoRecorrente);

  const queryResult = useGraphQL(RecurringAppointmentDetailPageQuery, {
    codAgendamentoRecorrente: codRecorrencia,
  });
  const { flags, user } = useRouteContext({ from: "__root__" });
  const navigate = useNavigate();
  const router = useRouter();
  const search = Route.useSearch();
  const setFlowOriginIndex = useSetAtom(recurringFlowOriginIndexAtom);

  const { data, refetch } = queryResult;

  const currentAppointmentId = search.appointmentId ?? 0;

  const recurrence = data?.agendamentosRecorrentes?.[0] ?? null;

  const appointments: RecurrenceAppointmentData[] = useMemo(() => {
    const roots = (data?.agendamentos ?? []).map(mapNodeToRaw);
    return buildRecurrenceSlots(roots, codRecorrencia).sort((a, b) =>
      a.data.localeCompare(b.data),
    );
  }, [data?.agendamentos, codRecorrencia]);

  const patientPhone =
    data?.agendamentos?.find((appointment) => appointment.Paciente?.telefone)?.Paciente
      ?.telefone ?? null;

  // Teto é por paciente (todas as recorrências ativas dele), não do usuário.
  const codPaciente = recurrence?.codPaciente ?? null;
  const limitsResult = useGraphQL(
    RecurringPatientLimitsQueryDocument,
    { codPacientes: codPaciente != null ? [codPaciente] : [] },
    { enabled: codPaciente != null },
  );
  const patientLimit =
    limitsResult.data?.limitesRecorrenciaPorPaciente?.find(
      (limit) => limit?.codPaciente === codPaciente,
    ) ?? null;
  const limite = patientLimit?.limite ?? null;
  const disponivel = patientLimit?.disponivel ?? 0;
  const utilizado = limite != null ? limite - disponivel : 0;

  const isCvRecurrence =
    recurrence != null && isConsultorioVirtual(recurrence.codUnidade ?? 0);

  const podeEstender =
    flags["estender-agendamento-recorrente"] &&
    recurrence != null &&
    classifyRecurrenceStatus(recurrence.status) === "emUso" &&
    (disponivel > 0 || isCvRecurrence);

  const handleAddAppointments = (): void => {
    setFlowOriginIndex(getHistoryIndex());
    trackEvent("Adicionar Agendamento Recorrente Clicado", {
      codUsuario: user.codUsuario,
      codAgendamentoRecorrente: codRecorrencia,
    });
    void navigate({
      to: "/recurring-appointments/extend/$recurringAppointmentId",
      params: { recurringAppointmentId: codAgendamentoRecorrente },
    });
  };

  const handleAppointmentClick = (codAgendamento: number): void => {
    void navigate({
      to: ".",
      search: { action: "appointment-details", appointmentId: codAgendamento },
    });
  };

  // Abre o mesmo drawer da agenda (detalhes → reagendar/cancelar) via search params.
  const handleClickCancelAppointment = (
    appointmentId: number,
    shouldDisplayFlexiblePriceDrawer: boolean,
    isRecurring: boolean,
  ): void => {
    if (shouldDisplayFlexiblePriceDrawer) {
      void navigate({
        to: ".",
        search: { action: "cancel-flexible-price-appointment", appointmentId },
      });
    } else if (isRecurring) {
      // Recorrente: abre a tela de escopo (só este agendamento ou toda a recorrência).
      void router.navigate({
        to: "/appointment/$appointmentId/cancel-recurrence",
        params: { appointmentId },
      });
    } else {
      void navigate({
        to: ".",
        search: { action: "cancel-appointment", appointmentId },
      });
    }
  };

  const handleCloseAppointmentDrawer = (): void => {
    const keepOpenFor = [
      "cancel-appointment",
      "expand-avatar-image",
      "reschedule-flexible-price-appointment",
      "cancel-flexible-price-appointment",
    ];

    if (keepOpenFor.includes(search.action ?? "")) return;

    // Abrir o drawer empilhou uma entrada no histórico; fechamos voltando nela
    // (em vez de empilhar um estado "limpo"), para o próximo "voltar" ir para a
    // listagem e não reabrir o drawer. Só volta se o drawer ainda está na URL,
    // evitando voltar duas vezes quando o fechamento veio do botão voltar.
    if (search.action === "appointment-details") {
      router.history.back();
    }
  };

  const setShowAppointmentCancelDrawer = (showCancelDrawer: boolean): void => {
    if (!showCancelDrawer && search.action === "cancel-appointment") {
      router.history.back();
    }
  };

  const onFlexiblePriceAppointmentCancelSuccess = (): void => {
    void refetch();
    // Volta o cancelamento e o drawer de detalhes, retornando à listagem.
    router.history.go(-2);
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Agendamentos recorrentes" align="center" />
      </HeaderRoot>
      <div className="flex flex-1 flex-col min-h-0 w-full mx-auto overflow-hidden md:max-w-[768px]">
        <AsyncDataWrapper {...queryResult}>
          {recurrence ? (
            <Page className="px-6 pt-0 pb-[calc(6rem+env(safe-area-inset-bottom))]">
              <div className="flex flex-col gap-4 pt-6 pb-4">
                <RecurrencePatientHeaderCard
                  patientName={recurrence.nomePaciente ?? ""}
                  patientPhone={patientPhone}
                  codPaciente={recurrence.codPaciente ?? null}
                  status={recurrence.status}
                  scheduleLabel={formatRecurrenceScheduleLabel(recurrence, {
                    includeFrequency: false,
                  })}
                />
                {limite != null && !isCvRecurrence ? (
                  <RecurrencePatientLimitCard
                    utilizado={utilizado}
                    limite={limite}
                    disponivel={disponivel}
                    onAddAppointments={podeEstender ? handleAddAppointments : undefined}
                  />
                ) : null}
              </div>
              <RecurrenceAppointmentTabs
                appointments={appointments}
                schedulePattern={recurrence}
                recurrenceCodUnidade={recurrence.codUnidade}
                onAppointmentClick={handleAppointmentClick}
                onAddAppointments={podeEstender ? handleAddAppointments : undefined}
              />
            </Page>
          ) : null}
        </AsyncDataWrapper>
      </div>
      {currentAppointmentId ? (
        <>
          <AppointmentDrawer
            handleAppointmentCancel={handleClickCancelAppointment}
            handleCloseAppointmentDrawer={handleCloseAppointmentDrawer}
            returnToRecurrenceOnReschedule
          />
          <AppointmentCancelDrawer
            codAgendamento={currentAppointmentId}
            setShowDrawer={setShowAppointmentCancelDrawer}
            showDrawer={search.action === "cancel-appointment"}
          />
          <FlexiblePriceAppointmentCancelDrawer
            appointmentId={currentAppointmentId}
            onCancelSuccess={onFlexiblePriceAppointmentCancelSuccess}
          />
          <FlexiblePriceAppointmentRescheduleDrawer
            appointmentId={currentAppointmentId}
          />
        </>
      ) : null}
    </>
  );
};

export const Route = createFileRoute("/recurring-appointments/$codAgendamentoRecorrente")(
  {
    component: RecurringAppointmentDetailPage,
    validateSearch: z.object({
      action: z.string().optional(),
      appointmentId: z.number().optional(),
    }),
  },
);
